import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, UserPlus } from 'lucide-react';
import { startLogin } from '../utils/authProvider';

/**
 * AuthPrompt - Reusable component for showing Sign In / Sign Up options
 * 
 * Props:
 * - icon: React component for the icon
 * - title: string
 * - description: string
 * - lang: 'en' | 'es'
 * - redirectAfterAuth: string (URL to redirect after successful auth)
 */
const AuthPrompt = ({ 
  icon: Icon, 
  title, 
  description, 
  lang = 'en',
  redirectAfterAuth = '/analyze'
}) => {
  const navigate = useNavigate();
  
  const handleGoogleAuth = () => {
    // Provider (emergent | google) resolved at runtime from /api/auth/config.
    startLogin(redirectAfterAuth || '/analyze');
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center max-w-md mx-auto">
      {Icon && (
        <div className="p-4 bg-blue-100 rounded-full w-fit mx-auto mb-4">
          <Icon className="w-10 h-10 text-blue-600" />
        </div>
      )}
      
      <h2 className="text-xl font-bold text-slate-800 mb-2">
        {title}
      </h2>
      
      <p className="text-slate-500 mb-6">
        {description}
      </p>
      
      <div className="space-y-3">
        {/* Primary: Sign Up / Create Account */}
        <button
          onClick={handleGoogleAuth}
          className="w-full px-6 py-3 bg-secondary text-white rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-sm"
          data-testid="auth-signup-btn"
        >
          <UserPlus className="w-5 h-5" />
          {lang === 'en' ? 'Create Free Account' : 'Crear Cuenta Gratis'}
        </button>
        
        {/* Secondary: Sign In */}
        <button
          onClick={handleGoogleAuth}
          className="w-full px-6 py-3 bg-white text-slate-700 border border-slate-300 rounded-lg font-medium hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
          data-testid="auth-signin-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {lang === 'en' ? 'Sign In with Google' : 'Iniciar Sesión con Google'}
        </button>
      </div>
      
      <p className="text-xs text-slate-400 mt-4">
        {lang === 'en' 
          ? 'Free account includes 3 analyses per month'
          : 'La cuenta gratuita incluye 3 análisis por mes'}
      </p>

      {/* The agreement is made HERE, at account creation — so the documents are named
          next to the Google buttons, not only in a footer two screens away. */}
      <p className="text-xs text-slate-500 mt-3 leading-relaxed" data-testid="auth-legal-consent">
        {lang === 'en' ? "By continuing, you agree to CHEKSTE's " : 'Al continuar, aceptas los '}
        <Link to="/terms" className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2">
          {lang === 'en' ? 'Terms of Service' : 'Términos de Servicio'}
        </Link>
        {lang === 'en' ? ' and acknowledge the ' : ' de CHEKSTE y reconoces la '}
        <Link to="/privacy" className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2">
          {lang === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
        </Link>
        .
      </p>
    </div>
  );
};

export default AuthPrompt;
